/**
 * quickEmailHandoff — pure-ish utilities for the three actions the
 * Quick Email modal can fire:
 *
 *   openInEmailClient(draft) — build a mailto: URL and window.open it.
 *                              Best-effort (mailto can't carry attachments,
 *                              has a ~2000 char URL limit, handles newlines
 *                              differently across clients).
 *   copyToClipboard(draft)   — copy "Subject: ...\n\n<body>" to clipboard.
 *                              Universal fallback for Apple Mail / Outlook
 *                              / Superhuman / HEY / whatever.
 *   logHandoff(supabase, ctx) — write one row to activity_log so the
 *                              lead / event timeline shows the draft.
 *
 * We deliberately don't return rich success/failure objects; callers
 * wire these into toasts and decide what to do. The functions throw
 * on programmer error (missing draft) but swallow Supabase/clipboard
 * failures — the user can always retry.
 */

/** Max mailto: URL length before we fall back to clipboard copy. */
const MAILTO_SAFE_LEN = 1800

/**
 * Build a mailto: URL.
 * Gmail, Apple Mail, and Outlook-for-web all accept mailto; desktop
 * Outlook swallows leading newlines so we trim the body.
 *
 * Returns { url, tooLong } — callers should branch if tooLong.
 */
export function buildMailtoUrl({ to = [], cc = [], bcc = [], subject = '', body = '' }) {
  const encode = (s) => encodeURIComponent(String(s || ''))
  const toList = Array.isArray(to) ? to.filter(Boolean).join(',') : String(to || '')
  const params = []
  if (cc.length)  params.push(`cc=${cc.map(encode).join(',')}`)
  if (bcc.length) params.push(`bcc=${bcc.map(encode).join(',')}`)
  if (subject)    params.push(`subject=${encode(subject)}`)
  if (body)       params.push(`body=${encode(body.replace(/^\n+/, ''))}`)
  const qs = params.length ? `?${params.join('&')}` : ''
  const url = `mailto:${encodeURIComponent(toList).replace(/%40/g, '@').replace(/%2C/g, ',')}${qs}`
  return { url, tooLong: url.length > MAILTO_SAFE_LEN }
}

/**
 * Open the draft in the user's default email client.
 * Uses location.href rather than window.open because mailto:// in a
 * new tab briefly shows an about:blank page on Chrome/Safari which
 * feels janky. Side effect: current tab stays put (mailto handlers
 * don't navigate away).
 */
export function openInEmailClient(draft) {
  const { url, tooLong } = buildMailtoUrl(draft)
  if (tooLong) {
    // Copy to clipboard as a backup — the user can paste if mailto
    // truncates. Don't block the open; most clients will still take
    // the truncated version.
    copyToClipboard(draft).catch(() => {})
  }
  // location.assign feels gentler than .href = because back button
  // still works on all browsers we support.
  window.location.assign(url)
  return { tooLong }
}

/**
 * Format the draft as a plain-text blob and copy it to the clipboard.
 * Returns a promise so callers can await to trigger a toast on resolve.
 */
export async function copyToClipboard({ subject = '', body = '', to = [] } = {}) {
  const toStr = Array.isArray(to) ? to.filter(Boolean).join(', ') : String(to || '')
  const lines = []
  if (toStr)   lines.push(`To: ${toStr}`)
  if (subject) lines.push(`Subject: ${subject}`)
  lines.push('', body || '')
  const text = lines.join('\n')

  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // fall through to execCommand
    }
  }

  // Legacy fallback for non-secure contexts
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
    return true
  } catch (err) {
    console.warn('[QuickEmail] clipboard copy failed:', err)
    return false
  }
}

/**
 * Write one activity_log row marking that the planner handed off a
 * draft to their email client (or copied it). Swallows errors — this
 * is supplementary accounting, not something to block the UX on.
 *
 * @param supabase        the supabase client
 * @param {object} ctx    {
 *   userId           required — planner's auth user id
 *   leadId           optional — lead row id (if pre-event)
 *   eventId          optional — event row id (if booked)
 *   eventName        optional — "Martinez Wedding"
 *   templateKey      required — which template fired, e.g. "lead_reply"
 *   templateLabel    human label e.g. "First reply"
 *   toCount          number of recipients
 *   subject          subject line (we stash it in task_label)
 *   method           "mailto" | "copy"
 *   recipientNames   array of names for metadata (optional)
 * }
 */
export async function logHandoff(supabase, ctx = {}) {
  if (!supabase || !ctx.userId) return
  const row = {
    user_id: ctx.userId,
    lead_id: ctx.leadId || null,
    event_id: ctx.eventId || null,
    event_name: ctx.eventName || null,
    task_label: ctx.subject ? ctx.subject.slice(0, 240) : null,
    assignee: 'Planner',
    action: 'email_handoff',
    occurred_at: new Date().toISOString(),
    metadata: {
      template_key: ctx.templateKey || null,
      template_label: ctx.templateLabel || null,
      to_count: ctx.toCount || 0,
      method: ctx.method || 'mailto',
      recipients: Array.isArray(ctx.recipientNames) ? ctx.recipientNames : undefined,
    },
  }
  try {
    await supabase.from('activity_log').insert(row)
  } catch (err) {
    // Metadata column might not exist yet in dev — fall back to the
    // pre-migration shape so logging still works.
    if (/column .*metadata/.test(String(err?.message))) {
      const fallback = { ...row }
      delete fallback.metadata
      delete fallback.lead_id
      try { await supabase.from('activity_log').insert(fallback) }
      catch { /* swallow — logging is best-effort */ }
      return
    }
    console.warn('[QuickEmail] activity_log insert failed:', err)
  }
}
